'use client';

import * as React from 'react';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { SustainabilityFocusSectionData } from '@/types/sustainability';

const SECTION_IMAGE_FALLBACK = '/images/stand_env2.png';

function DualCheckBulletIcon() {
  return (
    <span
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black text-white sm:h-9 sm:w-9"
      aria-hidden
    >
      <span className="flex translate-x-[0.5px] items-center gap-px">
        <Check className="h-[9px] w-[9px] sm:h-[11px] sm:w-[11px]" strokeWidth={3} />
        <Check
          className="-ml-[4px] h-[9px] w-[9px] sm:-ml-[5px] sm:h-[11px] sm:w-[11px]"
          strokeWidth={3}
        />
      </span>
    </span>
  );
}

export interface SustainabilityEnvironmentCircularityFocusProps {
  cmsSection?: SustainabilityFocusSectionData | null;
}

export default function SustainabilityEnvironmentCircularityFocus({
  cmsSection,
}: SustainabilityEnvironmentCircularityFocusProps = {}) {
  const t = useTranslations('sustainability.pages.environment');

  const containerRef = React.useRef<HTMLDivElement>(null);
  const pinRef = React.useRef<HTMLDivElement>(null);
  const leftRef = React.useRef<HTMLDivElement>(null);
  const rightRef = React.useRef<HTMLDivElement>(null);
  const leftEyebrowRef = React.useRef<HTMLParagraphElement>(null);
  const leftTitleRef = React.useRef<HTMLParagraphElement>(null);

  const fallbackBullets = [
    t('circularityBullet1'),
    t('circularityBullet2'),
    t('circularityBullet3'),
    t('circularityBullet4'),
    t('circularityBullet5'),
  ];
  const bullets = cmsSection?.bullets?.length ? cmsSection.bullets : fallbackBullets;
  const heading = cmsSection?.heading || t('circularityHeading');
  const eyebrow = cmsSection?.eyebrow || t('circularityLeftEyebrow');
  const title = cmsSection?.title || t('circularityLeftTitle');
  const imageUrl = cmsSection?.imageUrl || SECTION_IMAGE_FALLBACK;
  const imageAlt = cmsSection?.imageAlt || t('circularityImageAlt');
  const usingCms = !!(cmsSection && cmsSection.bullets.length);

  React.useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    gsap.registerPlugin(ScrollTrigger);

    const container = containerRef.current;
    const pinEl = pinRef.current;
    const leftEl = leftRef.current;
    const rightEl = rightRef.current;
    const eyebrowEl = leftEyebrowRef.current;
    const titleEl = leftTitleRef.current;
    if (!container || !pinEl || !leftEl || !rightEl) return;

    gsap.set(leftEl, { width: '100%' });
    gsap.set(rightEl, { xPercent: 100 });
    if (eyebrowEl) {
      gsap.set(eyebrowEl, {
        y: -18,
        scale: 1,
        transformOrigin: '0% 100%',
      });
    }
    if (titleEl) gsap.set(titleEl, { scale: 1, transformOrigin: '0% 100%' });

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: 'top top',
          end: '+=130%',
          scrub: 1.15,
          pin: pinEl,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      tl.to(leftEl, { width: '50%', ease: 'none', duration: 0.5 }).to(
        rightEl,
        { xPercent: 0, ease: 'none', duration: 0.5 },
        '<',
      );

      if (eyebrowEl) {
        tl.to(
          eyebrowEl,
          {
            y: 10,
            scale: 0.88,
            ease: 'none',
            duration: 0.5,
            transformOrigin: '0% 100%',
          },
          '<',
        );
      }
      if (titleEl) {
        tl.to(
          titleEl,
          { scale: 0.78, ease: 'none', duration: 0.5, transformOrigin: '0% 100%' },
          '<',
        );
      }
    }, container);

    const onResize = () => {
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      ctx.revert();
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative bg-[#f4f4f2]"
      aria-labelledby="circularity-focus-heading"
    >
      <div className="lg:hidden">
        <div className="relative aspect-[4/3] w-full overflow-hidden sm:aspect-[16/9]">
          <CmsImage
            src={imageUrl}
            alt={imageAlt}
            fill
            className="object-cover object-[center_42%] sm:object-center"
            sizes="100vw"
            priority={false}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div className="pointer-events-none absolute inset-0 bg-black/18" aria-hidden />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/18 to-transparent p-5 sm:p-8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/95 sm:text-xs">
              {eyebrow}
            </p>
            <p className="mt-2 max-w-xl text-[1.65rem] font-bold leading-[1.08] text-white sm:text-3xl">
              {title}
            </p>
          </div>
        </div>

        <div className="px-4 py-8 sm:px-8 sm:py-10 md:py-12 ">
          <h2
            id="circularity-focus-heading"
            className="max-w-2xl text-[1.45rem] font-bold leading-snug text-neutral-900 sm:text-2xl md:text-[1.65rem]"
          >
            {heading}
          </h2>
          <ul className="mt-6 space-y-4 sm:mt-7">
            {bullets.map((text, i) => (
              <li key={i} className="flex gap-3 sm:gap-4">
                <DualCheckBulletIcon />
                <p
                  className="text-sm leading-relaxed text-neutral-700 sm:text-[0.9375rem] [&_strong]:font-semibold [&_strong]:text-neutral-900"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </li>
            ))}
            {!usingCms ? (
              <li className="flex gap-3 sm:gap-4">
                <DualCheckBulletIcon />
                <p className="text-sm leading-relaxed text-neutral-700 sm:text-[0.9375rem]">
                  {t('circularityBullet6Intro')}
                  <strong className="font-semibold text-neutral-900">
                    {t('circularityBullet6Total')}
                  </strong>
                </p>
              </li>
            ) : null}
          </ul>
        </div>
      </div>

      <div
        ref={pinRef}
        className="relative hidden h-[100dvh] w-full max-w-[100vw] overflow-hidden bg-[#f4f4f2] lg:block [@media(max-height:800px)]:min-h-[600px]"
      >
        <div
          ref={rightRef}
          className="absolute inset-y-0 right-0 z-[3] w-1/2 bg-[#ececea] [@media(max-height:800px)]:top-[var(--site-header-height)]"
        >
          <div className="flex h-full min-h-0 flex-col justify-center  px-6 py-10 sm:px-10 sm:py-12 md:px-14 md:py-16 lg:pr-16 min-[1280px]:max-[1410px]:scale-75">
            <h2
              id="circularity-focus-heading"
              className="max-w-xl text-xl font-bold leading-snug text-neutral-900 sm:text-2xl md:text-[1.65rem] md:leading-tight"
            >
              {heading}
            </h2>
            <ul className="mt-8 max-w-2xl space-y-5 md:space-y-6">
              {bullets.map((text, i) => (
                <li key={i} className="flex gap-4">
                  <DualCheckBulletIcon />
                  <p
                    className="text-sm leading-relaxed text-neutral-700 sm:text-[0.9375rem] [&_strong]:font-semibold [&_strong]:text-neutral-900"
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                </li>
              ))}
              {!usingCms ? (
                <li className="flex gap-4">
                  <DualCheckBulletIcon />
                  <p className="text-sm leading-relaxed text-neutral-700 sm:text-[0.9375rem]">
                    {t('circularityBullet6Intro')}
                    <strong className="font-semibold text-neutral-900">
                      {t('circularityBullet6Total')}
                    </strong>
                  </p>
                </li>
              ) : null}
            </ul>
          </div>
        </div>

        <div
          ref={leftRef}
          className="relative z-[2] h-full shrink-0 overflow-hidden"
          style={{ width: '100%' }}
        >
          <CmsImage
            src={imageUrl}
            alt={imageAlt}
            fill
            className="object-cover object-[center_42%] sm:object-center"
            sizes="100vw"
            priority={false}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div className="pointer-events-none absolute inset-0 z-[1] bg-black/18" aria-hidden />
          <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/45 via-black/12 to-transparent p-6 sm:p-10 md:p-12">
            <p
              ref={leftEyebrowRef}
              className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/95 sm:text-xs md:text-[0.8125rem]"
            >
              {eyebrow}
            </p>
            <p
              ref={leftTitleRef}
              className="mt-2.5 max-w-[min(100%,22rem)] text-2xl font-bold leading-[1.08] text-white sm:text-3xl md:text-[2.125rem] md:leading-[1.06]"
            >
              {title}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
